import { faMoneyBill } from '@fortawesome/pro-regular-svg-icons/faMoneyBill';
import { faFile, faFileSearch } from '@fortawesome/pro-light-svg-icons';
import { AppointmentListItem } from '@spike/appointments-list-model';
import { changeStatusAppointmentThunk } from '@spike/bookings-action';
import clsx from 'clsx';
import ListPetInfo from 'components/Bookings2/UI/ListPetInfo/ListPetInfo';
import StatusButton from 'components/OrderDetail/StatusButton';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { useHasPremiumAccess, usePermissions, useTimeZone } from 'hooks';
import { FunctionComponent, MouseEvent, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import styles from './DaycareTableRow.module.scss';
import NotesTableRow from './NotesTableRow';
import PaidIcon from './PaidIcon';
import DepositPendingIcon from './DepositPendingIcon';
import PetParent from './PetParent';
import Service from './Service';
import { status as appointmentStatus } from '@spike/appointment-model';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { EvaluationReportDrawer } from 'components/UI/EvaluationReportDrawer/EvaluationReportDrawer';
import { useApiClientWrapper } from 'hooks';
import { dispatch } from 'store';

interface AllTableRowProps {
    app: AppointmentListItem;
    isLast?: boolean;
    hideClientColumn?: boolean;
    scrollerContainerId: string;
    className?: string;
    onCheckout?: () => void;
    onShowOrder?: () => void;
    onShowClient?: () => void;
    onShowPet?: () => void;
    onViewReport?: () => void;
    onAddReport?: () => void;
}

const showReport = [
    appointmentStatus.READY_FOR_CHECK_OUT,
    appointmentStatus.PAYMENT,
    appointmentStatus.COMPLETED,
    appointmentStatus.DECLINED,
    appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
    appointmentStatus.IN_PROGRESS,
    appointmentStatus.BOOKED,
    appointmentStatus.CONFIRMED,
    appointmentStatus.CHECKED_IN,
    appointmentStatus.CANCELLED,
    appointmentStatus.NO_SHOW
];
const addReport = [
    appointmentStatus.READY_FOR_CHECK_OUT,
    appointmentStatus.PAYMENT,
    appointmentStatus.COMPLETED,
    appointmentStatus.DECLINED,
    appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
    appointmentStatus.IN_PROGRESS,
    appointmentStatus.BOOKED,
    appointmentStatus.CONFIRMED,
    appointmentStatus.CHECKED_IN
];

export const AllTableRow: FunctionComponent<AllTableRowProps> = props => {
    const hasReportsPremiumAccess = useHasPremiumAccess('groomming_reports');
    const apiClientWrapper = useApiClientWrapper();
    const [evaluationResults, setEvaluationResults] = useState<{ pendingStatusId?: number } | null>(null);

    const permissions = usePermissions();
    const isCheckoutAllowed = permissions.checkout.takePayments;

    const { isUserLoggedInMarketplace } = useOrganizationMarketplaces();
    const allowActions = isUserLoggedInMarketplace(props.app.marketplaceId);

    const isMobile = useMediaQuery({ query: '(max-width:600px)' });
    const timezone = useTimeZone();

    const hasNotes = props.app.notes.trim().length > 0;

    const checkoutOption = {
        id: 'checkout',
        label: 'Checkout',
        icon: faMoneyBill,
        onClick: () => props.onCheckout?.()
    };

    const viewReportOption = {
        id: 'view_report',
        label: 'View Report',
        icon: faFileSearch,
        onClick: () => props.onViewReport?.()
    };

    const addReportOption = {
        id: 'add_report',
        label: 'Add Report',
        icon: faFile,
        onClick: () => props.onAddReport?.()
    };

    const evaluationResultsOption = {
        id: 'evaluation_results',
        label: props.app.reports.evaluationReportId != null ? 'Edit Results' : 'Add Results',
        icon: props.app.reports.evaluationReportId != null ? faFileSearch : faFile,
        onClick: () => setEvaluationResults({})
    };

    const options = [
        isCheckoutAllowed ? checkoutOption : null,
        props.app.service.businessArea === 'evaluation' ? evaluationResultsOption : null,
        hasReportsPremiumAccess &&
        ['dental', 'grooming'].includes(props.app.service.businessArea) &&
        (props.app.reports.dentalReportId || props.app.reports.groomingReportId) &&
        showReport.includes(props.app.status.id)
            ? viewReportOption
            : null,
        !props.app.customer.isDeleted &&
        hasReportsPremiumAccess &&
        ['dental', 'grooming'].includes(props.app.service.businessArea) &&
        addReport.includes(props.app.status.id) &&
        props.app.service.hasReport &&
        props.app.reports.dentalReportId == null &&
        props.app.reports.groomingReportId == null
            ? addReportOption
            : null
    ].filter(option => option !== null);

    const clickHandler = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        props.onShowOrder?.();
    };

    const date = props.app.startAt.tz(timezone);

    const petInfo = (
        <ListPetInfo
            orderId={props.app.orderId}
            pet={props.app.pet}
            onClick={allowActions ? props.onShowPet : undefined}
        />
    );

    const service = (
        <Service
            showBusinessArea={true}
            service={props.app.service}
            petId={props.app.pet.id}
            marketplaceId={props.app.marketplaceId}
        />
    );

    const statusButton = (
        <StatusButton
            appointmentId={props.app.appointmentId}
            apppointmentMarketplaceId={props.app.marketplaceId}
            appointmentStatus={props.app.status}
            appointmentClosedAt={props.app.closedAt ?? undefined}
            appointmentCancelationReasonId={props.app.cancellationReasonId ?? null}
            appointmentTrainingType={props.app.training?.type}
            appointmentWalkingType={props.app.walking?.type}
            recurringUuid={props.app.recurringUuid ?? undefined}
            customerFirstName={props.app.customer.firstName}
            customerLastName={props.app.customer.lastName}
            reloadOrderAfterStatusChange={false}
            businessAreaId={props.app.service.businessArea}
            fullWidth={isMobile}
            scrollerContainerId={props.scrollerContainerId}
            position="fixed"
            hasEvaluationResult={props.app.reports.evaluationReportId != null}
            onEvaluationResultRequired={pendingStatusId => setEvaluationResults({ pendingStatusId })}
        />
    );

    const evaluationResultsDrawer = evaluationResults && (
        <EvaluationReportDrawer
            evaluationReportId={props.app.reports.evaluationReportId ?? undefined}
            appointmentId={props.app.appointmentId}
            serviceId={props.app.service.id}
            petId={props.app.pet.id}
            defaultViewMode="edit"
            onClose={() => setEvaluationResults(null)}
            onSave={() => {
                const pendingStatusId = evaluationResults.pendingStatusId;
                setEvaluationResults(null);

                if (pendingStatusId != null) {
                    dispatch(changeStatusAppointmentThunk(apiClientWrapper, props.app.appointmentId, pendingStatusId));
                }
            }}
        />
    );

    const notMobileRow = (
        <>
            <TableRow
                className={clsx(styles.row, { [styles.withoutBorder]: hasNotes }, props.className)}
                onClick={clickHandler}
            >
                {!props.hideClientColumn && (
                    <TableCell className={styles.clientNameCell}>
                        <PetParent
                            customer={props.app.customer}
                            marketplaceId={props.app.marketplaceId}
                            showOnly={!allowActions}
                            showTagsIcon={!isMobile}
                            onClick={props.onShowClient}
                        />
                    </TableCell>
                )}
                <TableCell>{petInfo}</TableCell>
                <TableCell>{service}</TableCell>
                <TableCell>{props.app.startAt.format('MMM DD, YYYY [at] h:mm a')}</TableCell>
                <TableCell className={styles.priceCell}>
                    ${Number(props.app.total).toFixed(2)}
                    <DepositPendingIcon show={props.app.bookingDepositPending} />
                    <PaidIcon paid={props.app.paid} />
                </TableCell>
                <TableCell
                    width={180}
                    className={styles.statusCell}
                >
                    {statusButton}
                </TableCell>
                <TableCell
                    isActions
                    onClick={event => event.stopPropagation()}
                >
                    {allowActions && (
                        <ThreeDotsDropdown
                            options={options}
                            direction={props.isLast ? 'up' : 'down'}
                        />
                    )}
                </TableCell>
            </TableRow>
            {hasNotes && (
                <NotesTableRow
                    notes={props.app.notes}
                    colSpan={7}
                />
            )}
        </>
    );

    const mobileRow = (
        <>
            <TableRow
                className={clsx(styles.mobileRow, props.className)}
                onClick={clickHandler}
            >
                <TableCell className={styles.mobileCell}>
                    <div className={styles.mobileHeader}>
                        <div className={styles.mobilePetContainer}>{petInfo}</div>
                        <div className={styles.mobileDateTimeContainer}>
                            <span className={styles.mobileDateTime}>{props.app.startAt.format('MMM DD, h:mm a')}</span>
                        </div>
                    </div>
                    <div className={styles.mobileBody}>{service}</div>
                    <div className={styles.mobileFooter}>
                        {hasNotes && (
                            <NotesTableRow
                                notes={props.app.notes}
                                colSpan={8}
                            />
                        )}
                        {statusButton}
                    </div>
                </TableCell>
            </TableRow>
        </>
    );

    return (
        <>
            {isMobile ? mobileRow : notMobileRow}
            {evaluationResultsDrawer}
        </>
    );
};

export default AllTableRow;
